import { RoutesPlannerTask } from '@bringg-frontend/eta-calculator';
import { buildTaskInventoriesHierarchy, calculateMaxLoad, LoadCalculationParams } from '@bringg/common-utils';
import { Run, Task, TaskInventory, VehicleType } from '@bringg/types';

export const getTaskInventoriesFromTasks = (tasks: Task[] | RoutesPlannerTask[]): TaskInventory[] => {
	const wayPointToIndex = {};
	return tasks
		.flatMap(task => buildTaskInventoriesHierarchy(task.task_inventories || []))
		.reduce((arr, taskInventory) => {
			const wayPointId = taskInventory.way_point_id;
			let index = wayPointToIndex[wayPointId];
			if (index == undefined) {
				index = arr.length;
				wayPointToIndex[wayPointId] = index;
				arr[index] = [];
			}
			arr[index].push(taskInventory);
			return arr;
		}, [])
		.flatMap(inventories => inventories.sort((x, y) => +x.pending - +y.pending));
};

export const getClusterModeTaskInventoriesFromTasks = (
	tasks: Task[] | RoutesPlannerTask[],
	customerIdByTaskInventory: { [key: string]: number }
): TaskInventory[] => {
	const taskInventories = getTaskInventoriesFromTasks(tasks);
	const customers = {};
	const newTaskInventoryIndices = {};
	return taskInventories
		.map((taskInventory, index) => {
			const customerId = customerIdByTaskInventory[taskInventory.id];
			let newIndex = customers[customerId];
			if (newIndex == undefined) {
				newIndex = index;
			}
			customers[customerId] = newIndex;
			newTaskInventoryIndices[taskInventory.id] = newIndex;
			return taskInventory;
		})
		.sort((x, y) => newTaskInventoryIndices[x.id] - newTaskInventoryIndices[y.id]);
};

export const getTaskInventories = (
	tasks: Task[] | RoutesPlannerTask[],
	isClusterMode: boolean,
	customerIdByTaskInventory: { [key: string]: number }
): TaskInventory[] => {
	const sortedTasks = tasks.sort((x, y) => x.priority - y.priority);

	return isClusterMode
		? getClusterModeTaskInventoriesFromTasks(sortedTasks, customerIdByTaskInventory)
		: getTaskInventoriesFromTasks(sortedTasks);
};

export const getCustomerIdByTaskInventory = tasks => {
	const customerIdByTaskInventory = {};
	tasks.forEach(task =>
		task.task_inventories.forEach(inventory => {
			const wayPointId = inventory.way_point_id;
			const customerId = task.way_points.find(wp => wp.id === wayPointId)?.customer_id;
			if (customerId) {
				customerIdByTaskInventory[inventory.id] = customerId;
			}
		})
	);
	return customerIdByTaskInventory;
};

export const calculateTaskInventoriesMaxLoad = (
	taskInventories: TaskInventory[],
	field: string,
	ignoreQuantity: boolean,
	uniqueInventoryFields: string[]
): number => {
	const loadCalculationParams: LoadCalculationParams<TaskInventory> = {
		items: taskInventories,
		field,
		ignoreQuantity,
		uniqueInventoryFields
	};
	return calculateMaxLoad(loadCalculationParams);
};

export const getMaxWeightForTasksList = (
	tasks: Task[],
	isClusterMode: boolean,
	ignoreQuantity: boolean,
	uniqueInventoryFields: string[]
) => {
	const customerIdByTaskInventory = getCustomerIdByTaskInventory(tasks);
	const items = getTaskInventories(tasks, isClusterMode, customerIdByTaskInventory);

	return calculateMaxLoad({ items, field: 'weight', ignoreQuantity, uniqueInventoryFields });
};

export const getVehiclesCapacity = (
	tasks: Task[],
	vehicleTypeByVehicleIds: { [key: string]: VehicleType },
	getUserTypeWeightCapacity: (tasks: Task[]) => number
) => {
	const uniqVehiclesIds = new Set<number>();
	tasks.forEach(task => uniqVehiclesIds.add(task.vehicle_id));
	let vehicleTotalWeight = 0;

	uniqVehiclesIds.forEach(vehicleId => {
		vehicleTotalWeight += vehicleTypeByVehicleIds[vehicleId]?.max_total_weight || 0;
	});
	return vehicleTotalWeight || getUserTypeWeightCapacity(tasks);
};

export const getWeightCapacity = (
	tasks: Task[],
	vehicleTypeByVehicleIds: { [key: string]: VehicleType },
	enableVehicles: boolean,
	getUserTypeWeightCapacity: (tasks?: Task[]) => number
) => {
	const weightCapacity = enableVehicles
		? getVehiclesCapacity(tasks, vehicleTypeByVehicleIds, getUserTypeWeightCapacity)
		: getUserTypeWeightCapacity(tasks);
	return weightCapacity;
};

export const getTasksWeight = (
	selectedTasks: Task[],
	ignoreQuantity: boolean,
	uniqueInventoryFields: string[],
	enableVehicles: boolean,
	vehicleTypeByVehicleIds: { [key: string]: VehicleType },
	getUserTypeWeightCapacity: (tasks: Task[]) => number,
	isClusterModeEnabled: boolean,
	run?: Run
): { maxWeight: number; weightCapacity: number } => {
	let maxWeight = 0;
	let weightCapacity = 0;
	if (run?.max_weight != undefined && run?.weight_capacity != undefined) {
		maxWeight = run.max_weight;
		weightCapacity = run.weight_capacity;
	} else if (selectedTasks.length) {
		maxWeight = getMaxWeightForTasksList(
			selectedTasks,
			isClusterModeEnabled,
			ignoreQuantity,
			uniqueInventoryFields
		);
		weightCapacity = getWeightCapacity(
			selectedTasks,
			vehicleTypeByVehicleIds,
			enableVehicles,
			getUserTypeWeightCapacity
		);
	}
	return { maxWeight, weightCapacity };
};
